// Tutorial step definitions for the onboarding experience
export const TUTORIAL_STEPS = [
  {
    id: 'welcome',
    element: null, // No specific element, centered modal
    popover: {
      title: '👋 Welcome to Neoshift BI!',
      description: `
        <div class="space-y-4">
          <p>Let's take a quick tour to help you get started with your Business Intelligence platform.</p>
          <p>You'll learn how to:</p>
          <ul class="list-disc list-inside space-y-2 ml-2">
            <li>Connect your data sources</li>
            <li>Build relationships in Data Studio</li>
            <li>Create beautiful dashboards</li>
            <li>Chat with your data using AI</li>
          </ul>
          <p class="text-sm text-gray-500">This will only take about 2 minutes.</p>
        </div>
      `,
      side: 'center',
      align: 'center'
    }
  },
  {
    id: 'data-sources',
    element: '[data-tour="nav-data-sources"]',
    popover: {
      title: '📊 Step 1: Connect Your Data',
      description: `
        <div class="space-y-3">
          <p>Start by connecting your data sources here. We support:</p>
          <ul class="list-disc list-inside space-y-1 ml-2">
            <li>Google Sheets</li>
            <li>CSV/Excel files</li>
            <li>Databases (PostgreSQL, MySQL, BigQuery)</li>
            <li>REST APIs</li>
          </ul>
          <p class="font-medium">Click "Data Sources" to continue →</p>
        </div>
      `,
      side: 'right',
      align: 'start'
    },
    action: 'highlight'
  },
  {
    id: 'data-studio',
    element: '[data-tour="nav-data-studio"]',
    popover: {
      title: '✨ Step 2: Data Studio',
      description: `
        <div class="space-y-3">
          <p>After connecting your data sources, use <strong>Data Studio</strong> to:</p>
          <ul class="list-disc list-inside space-y-1 ml-2">
            <li>Map relationships between tables</li>
            <li>Create unified views of your data</li>
            <li>Build your semantic layer</li>
          </ul>
          <p class="text-sm text-gray-600">This makes querying your data much easier and more powerful!</p>
        </div>
      `,
      side: 'right',
      align: 'start'
    },
    action: 'highlight'
  },
  {
    id: 'dashboards',
    element: '[data-tour="nav-dashboards"]',
    popover: {
      title: '📈 Step 3: Build Dashboards',
      description: `
        <div class="space-y-3">
          <p>Create interactive dashboards to visualize your data:</p>
          <ul class="list-disc list-inside space-y-1 ml-2">
            <li>Drag-and-drop chart builder</li>
            <li><strong>AI Chart Builder</strong> - Create charts using natural language instead of complex SQL</li>
            <li>Multiple chart types (bar, line, pie, etc.)</li>
            <li>Real-time data updates</li>
            <li>Share with your team</li>
          </ul>
        </div>
      `,
      side: 'right',
      align: 'start'
    },
    action: 'highlight'
  },
  {
    id: 'ai-assistant',
    element: '[data-tour="nav-ai-assistant"]',
    popover: {
      title: '🤖 Step 4: AI Assistant',
      description: `
        <div class="space-y-3">
          <p>Ask questions about your data in plain English:</p>
          <div class="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg text-sm space-y-2">
            <p class="italic">"What were my top 10 customers last month?"</p>
            <p class="italic">"Show me sales trends by region"</p>
            <p class="italic">"Compare this quarter vs last quarter"</p>
          </div>
          <p class="text-sm">The AI will generate charts and insights automatically!</p>
        </div>
      `,
      side: 'right',
      align: 'start'
    },
    action: 'highlight'
  },
  {
    id: 'complete',
    element: null,
    popover: {
      title: '🎉 You\'re All Set!',
      description: `
        <div class="space-y-4">
          <p>You now know how to navigate Neoshift BI!</p>
          <p><strong>Next steps:</strong></p>
          <ol class="list-decimal list-inside space-y-2 ml-2">
            <li>Connect your first data source</li>
            <li>Create relationships in Data Studio</li>
            <li>Build your first dashboard</li>
            <li>Try the AI Assistant</li>
          </ol>
          <p class="text-sm text-gray-500">Need help? Click the <strong>Help</strong> button in the sidebar anytime.</p>
        </div>
      `,
      side: 'center',
      align: 'center'
    }
  }
];

// Tutorial configuration
export const TUTORIAL_CONFIG = {
  animate: true,
  showProgress: true,
  allowKeyboardControl: true,
  smoothScroll: true,
  stagePadding: 10,
  stageRadius: 8,
  overlayColor: 'rgba(0, 0, 0, 0.75)',
  popoverClass: 'tutorial-popover',
  progressText: '{{current}} of {{total}}',
  nextBtnText: 'Next →',
  prevBtnText: '← Back',
  doneBtnText: 'Get Started!',
  showButtons: ['next', 'previous', 'close'],
  
  // Custom button labels
  onNextClick: null, // Set dynamically
  onPrevClick: null, // Set dynamically
  onCloseClick: null // Set dynamically
};

// Helper to check if a step should be shown based on completed steps
export const shouldShowStep = (stepId, completedSteps) => {
  // Always show welcome and complete
  if (stepId === 'welcome' || stepId === 'complete') return true;
  
  // Don't show steps that are already completed
  return !completedSteps.includes(stepId);
};
